import React from 'react';
import styled from '@emotion/styled';
import { Grid, GridItem, Hidden } from '../../helpers';
import Spacer from '../../helpers/Spacer';
import TeaserCard from '../../components/TeaserCard/TeaserCard';
import { ModuleHeader, Link } from '../../primitives';
import SearchToolbar from '../SearchToolbar/SearchToolbar';
import MagneticField from '../../components/MagneticField';
import { mixins } from '../../core/styles/theme/default';
import { rem } from 'polished';

const StyledTeaserFunding = styled.aside`
  display: block;
  position: relative;
`;

const StyledDecoration = styled.div`
  position: absolute;
  top: ${rem(-48)};
  right: 0;
`;

const StyledLink = styled(Link)`
  .m-richtext & {
    text-decoration: none;
  }
`;

const StyledGridItem = styled(GridItem)`
  ${mixins.responsiveStyles({
    'margin-bottom': {
      zero: `${rem(16)}`,
      medium: `${rem(24)}`,
      ultra: `${rem(32)}`,
    },
  })}
`;

const TeaserFunding = (teaserFundingProps: TeaserFundingProps) => {
  if (teaserFundingProps === null) return null;

  const {
    title,
    lead,
    action,
    items,
    hideMoreLink,
    hideMagneticField,
    displaySearch,
    selectAllLabel,
    topicOptions,
    labels,
    locale,
    moreLink,
  } = teaserFundingProps;

  return (
    <StyledTeaserFunding data-anim="orange">
      {!hideMagneticField ? (
        <Hidden below="large">
          <StyledDecoration>
            <MagneticField isVisible />
          </StyledDecoration>
        </Hidden>
      ) : null}

      <Grid columns={12}>
        <GridItem size={{ zero: 12, ultra: 8 }} push={{ zero: 0, ultra: 2 }}>
          <ModuleHeader title={title || ''} lead={lead || ''} />
          <Spacer space="deci" />

          {displaySearch !== false ? (
            <>
              <SearchToolbar
                options={topicOptions}
                inputLabel={labels.inputLabel}
                inputPlaceholder={labels.inputPlaceholder}
                searchFilterText={labels.topicText}
                searchButtonText={labels.searchButtonText}
                selectAllLabel={selectAllLabel}
                action={action}
                isMulti={false}
                renderSelectedValue={true}
                onSearch={(event, searchValue, checkedSearchFilters) => {
                  event.preventDefault();

                  let foundingWhat = 'building';
                  let foundingWhom = 'personal';

                  if (checkedSearchFilters.indexOf('mobility') > -1) {
                    foundingWhat = 'mobility';
                  }

                  if (checkedSearchFilters.indexOf('business') > -1) {
                    foundingWhom = 'business';
                  }

                  window.open(
                    `https://energiefranken.ch/${locale}/${searchValue}/${foundingWhat}/${foundingWhom}`,
                    '_blank',
                  );
                }}
              />
              <Spacer space="peta" />
            </>
          ) : null}

          {items?.length ? (
            <Grid columns={12}>
              {items.map((data: any, j: number) => (
                <StyledGridItem
                  size={{ zero: 12, medium: 6, wide: 4, ultra: 6 }}
                  push={0}
                  key={j}
                >
                  <TeaserCard data={data} variant="funding" key={j} />
                </StyledGridItem>
              ))}
            </Grid>
          ) : null}

          {labels.moreLink && !hideMoreLink ? (
            <>
              <Spacer space="beta" />
              <StyledLink
                linkText={labels.moreLink}
                link={{
                  href: moreLink || '',
                  title: labels.moreLink,
                }}
                variant="internal"
              />
            </>
          ) : null}
        </GridItem>
      </Grid>
    </StyledTeaserFunding>
  );
};

export type TeaserFundingProps = {
  items?: any;
  title?: string;
  lead?: string;
  action?: string;
  hideMoreLink?: boolean;
  hideMagneticField?: boolean;
  displaySearch?: boolean;
  topicOptions: any;
  selectAllLabel: string;
  labels: any;
  locale: string;
  moreLink?: string;
} | null;

export default TeaserFunding;
